import { Card, Input_, Main_, Main_Loader, Main_Plain, PageFull, PageTitle } from 'monica-alexandria';
import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { Board_View_Toggle } from '../boardView/Board_View_Toggle';
import Offers_Board_Card from './Offers_Board_Card';
import config from '../../../config';
import { offer_cerebro_task_url, offer_has_pending_cerebro_phase } from '../../../helpers/preoffer_handoff';
import {
    list_legacy_board_offer_page,
    list_agency_board_offer_page,
    delete_fake_offer_by_id,
    retry_agency_preoffer_sync_by_id
} from '../../../services/api/Offers';
import {
    offer_client_name_from_offer,
    offer_current_status,
    offer_emails_from_offer,
    offer_reference_from_offer,
    offer_type_label_from_offer,
    offer_visit_count,
    selected_types_from_offer
} from '../../../helpers/offerObject';
import { merge_offer_board_rows, offer_board_identity } from '../../../helpers/offerBoardRows';
import {
    cerebro_connection_status,
    offer_card_shows_visits,
    offer_completed_creator_profile,
    offer_creator_profile,
    offer_review_action,
    offer_review_stage,
    offer_reviewer_profile,
    offer_overview_path,
    offer_should_open_as_draft,
    preoffer_sync_can_retry,
    preoffer_sync_error,
    preoffer_sync_status,
    sort_offers_by_created_at
} from './offers_board_rules';

const OffersBoardContent = styled(PageFull)`
    display: grid;
    gap: 1rem;
`;

const OffersBoardHeader = styled.div`
    display: flex;
    align-items: center;
`;

const OffersBoardActions = styled.div`
    display: flex;
    align-items: center;
    gap: 1rem;
    justify-content: space-between;
    margin-bottom: 1rem;
`;

const OffersBoardSearch = styled.div`
    min-width: 26rem;
    margin-bottom: var(--smallPads);

    input {
        padding-top: 1rem;
    }

    label {
        display: none;
    }
`;

const OffersBoardStage = styled.div`
    position: relative;
    min-height: 42rem;
`;

const OffersBoardColumns = styled.div`
    display: grid;
    grid-template-columns: repeat(${p => p.columnCount}, minmax(22rem, 1fr));
    gap: 1.2rem;
    overflow-x: auto;
    padding-bottom: 1.5rem;
`;

const OffersBoardColumn = styled(Card)`
    height: calc(100vh - 27rem);
    min-height: 42rem;
    padding: 1.2rem;
    display: flex;
    flex-direction: column;
    gap: 1rem;
    border: 0.2rem solid ${p => p.theme.mid};
    overflow: auto;
`;

const OffersBoardColumnHeader = styled.div`
    position: relative;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 0.8rem;
`;

const OffersBoardColumnTitle = styled.div`
    display: grid;
    gap: 0.2rem;

    h4,
    h6 {
        margin: 0;
    }
`;

const OffersBoardColumnCount = styled.div`
    padding: 0.4rem 0.8rem;
    border-radius: var(--smallRadius);
    background: ${p => p.theme.low};
`;

const OffersBoardColumnBody = styled.div`
    display: flex;
    flex-direction: column;
    gap: 1rem;
`;

const OffersBoardEmpty = styled(Card)`
    padding: var(--normalPads);
    background: ${p => p.theme.low};
`;

const OffersBoardTableWrap = styled(Card)`
    padding: 0;
    overflow: auto;
    height: calc(100vh - 27rem);
    min-height: 42rem;
`;

const OffersBoardTable = styled.table`
    width: 100%;
    min-width: 138rem;
    border-collapse: collapse;

    th,
    td {
        padding: 1rem;
        border-bottom: 0.1rem solid ${p => p.theme.mid};
        text-align: left;
        vertical-align: top;
    }

    th {
        position: sticky;
        top: 0;
        z-index: 1;
        background: ${p => p.theme.low};
    }

    tbody tr:hover {
        background: ${p => p.theme.low};
    }
`;

const OffersBoardTableHeaderButton = styled.button`
    width: 100%;
    border: none;
    padding: 0;
    background: transparent;
    color: ${p => p.theme.color};
    cursor: pointer;
    text-align: left;

    h6 {
        margin: 0;
        font-family: "fontBold";
    }
`;

const OffersBoardTableText = styled.div`
    display: flex;
    flex-direction: column;
    gap: 0.3rem;
    min-width: 0;

    h5,
    h6,
    p {
        margin: 0;
        overflow-wrap: anywhere;
    }

    h6 {
        color: ${p => p.theme.high};
    }
`;

const OffersBoardTableActions = styled.div`
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 0.6rem;
`;

const OffersBoardTableEmpty = styled.div`
    padding: var(--normalPads);
`;

const agencyBoardColumns = [
    {
        key: 'uncompleted',
        title: 'UNCOMPLETED',
        subtitle: 'submit'
    },
    {
        key: 'reviewed',
        title: 'REVIEWED',
        subtitle: 'review'
    },
    {
        key: 'created',
        title: 'VALID',
        subtitle: 'created'
    },
    {
        key: 'initialised',
        title: 'INITIALISED',
        subtitle: ''
    },
    {
        key: 'paid',
        title: 'PAID',
        subtitle: ''
    },
    {
        key: 'lost',
        title: 'LOST',
        subtitle: 'invalid'
    }
];

const empty_column_pagination = () => {
    return agencyBoardColumns.reduce((pagination, columnItem) => {
        return {
            ...pagination,
            [columnItem.key]: {
                nextCursor: '',
                hasMore: false
            }
        };
    }, {});
};

const search_params = (searchValue = '') => {
    const cleanSearchValue = `${searchValue || ''}`.trim();

    if (!cleanSearchValue.length) {
        return {};
    }

    return {
        search: cleanSearchValue
    };
};

const read_normalized_value = (value = '') => {
    return `${value || ''}`.trim().toLowerCase();
};

const read_array = (value) => {
    return Array.isArray(value) ? value : [];
};

const offer_identity_value = (offerItem = {}) => {
    return offer_board_identity(offerItem);
};

const offer_preview_identity_value = (offerItem = {}) => {
    return `${offerItem?.offerId || offerItem?._id || offerItem?.ofrId || offer_reference(offerItem) || ''}`.trim();
};

const open_offer = (offerItem = {}, boardStatus = '', routeLanguage = 'el', isLegacyBoard = false, navigate) => {
    const offerId = offer_preview_identity_value(offerItem);

    if (!offerId.length) {
        return;
    }

    if (!isLegacyBoard) {
        navigate(offer_overview_path(offerId, routeLanguage));
        return;
    }

    if (offer_should_open_as_draft(offerItem, boardStatus, isLegacyBoard)) {
        window.location.assign(`/${routeLanguage}/new-offer?offerId=${encodeURIComponent(offerId)}&continueOffer=1`);
        return;
    }

    window.open(`/${routeLanguage}/offer/${encodeURIComponent(offerId)}`, '_blank', 'noopener,noreferrer');
};

const read_preoffer_value = (offerItem = {}, fieldName = '') => {
    const values = [
        offerItem?.typesData?.[fieldName],
        offerItem?.setupData?.[fieldName],
        offerItem?.[fieldName]
    ];

    return values.map((valueItem) => `${valueItem || ''}`.trim()).find((valueItem) => valueItem.length) || '';
};

const preoffer_turn = (offerItem = {}) => {
    return read_normalized_value(
        read_preoffer_value(offerItem, 'preofferTurn')
        || read_preoffer_value(offerItem, 'preofferCurrentTurn')
    );
};

const workflow_turn_label = (offerItem = {}) => {
    const turn = read_normalized_value(
        offerItem?.workflow?.turn
        || read_preoffer_value(offerItem, 'preofferTurn')
    );

    if (['preparer', 'team_lead', 'teamleader', 'team_leader', 'teamlead'].includes(turn)) {
        return 'Review';
    }

    if (['creator', 'coordinator'].includes(turn)) {
        return 'Create';
    }

    return '';
};

const preoffer_sent_or_scheduled = (offerItem = {}) => {
    return `${read_preoffer_value(offerItem, 'preofferSentAt') || offerItem?.preofferSentAt || ''}`.trim().length > 0
        || `${read_preoffer_value(offerItem, 'preofferScheduledAt') || offerItem?.preofferScheduledAt || ''}`.trim().length > 0;
};

const request_task_status = (offerItem = {}) => {
    const normalizedStatus = read_normalized_value(offerItem?.status);
    const currentTurn = preoffer_turn(offerItem);
    const requestedAt = `${read_preoffer_value(offerItem, 'preofferRequestedAt') || ''}`.trim();
    const statusAllowsPreoffer = normalizedStatus.length === 0
        || normalizedStatus === 'draft'
        || normalizedStatus === 'ready_for_review';

    if (!statusAllowsPreoffer || !requestedAt.length || preoffer_sent_or_scheduled(offerItem)) {
        return '';
    }

    if (currentTurn === 'team_lead') {
        return 'help';
    }

    if (currentTurn === 'coordinator') {
        return 'requested';
    }

    return '';
};

const offer_reference = (offerItem = {}) => {
    return offer_reference_from_offer(offerItem);
};

const offer_brand = (offerItem = {}) => {
    return offer_client_name_from_offer(offerItem);
};

const offer_email = (offerItem = {}) => {
    const emails = offer_emails_from_offer(offerItem);

    return emails[0] || '';
};

const offer_creator = (offerItem = {}) => {
    return offer_creator_profile(offerItem);
};

const offer_creator_id = (offerItem = {}) => {
    const creator = offer_creator(offerItem);

    return creator.id;
};

const offer_creator_label = (offerItem = {}) => {
    const creator = offer_creator(offerItem);
    return creator.label;
};

const offer_reviewer_label = (offerItem = {}) => {
    return offer_reviewer_profile(offerItem).label;
};

const offer_completed_creator_label = (offerItem = {}) => {
    return offer_completed_creator_profile(offerItem).label;
};

const selected_offer_types = (offerItem = {}) => {
    return selected_types_from_offer(offerItem);
};

const offer_type_label = (offerItem = {}) => {
    return offer_type_label_from_offer(offerItem);
};

const offer_type_key = (typeValue = '') => {
    const normalizedType = read_normalized_value(typeValue);

    if (normalizedType === 'graphic') {
        return 'creative';
    }

    return normalizedType;
};

const offer_type_keys_for_lookup = (typeValue = '') => {
    const normalizedType = offer_type_key(typeValue);

    if (normalizedType === 'creative') {
        return ['creative', 'graphic'];
    }

    return normalizedType.length ? [normalizedType] : [];
};

const has_positive_payment_value = (values = []) => {
    return values.some((value) => {
        const numberValue = Number(value);

        return Number.isFinite(numberValue) && numberValue > 0;
    });
};

const offer_payment_profile = (offerItem = {}) => {
    const priceData = offerItem?.priceData || {};
    const paymentState = offerItem?.paymentState || {};
    const recurringPaymentLinkUrl = `${priceData?.recurringPaymentLinkUrl || ''}`.trim();
    const oneOffPaymentLinkUrl = `${priceData?.oneOffPaymentLinkUrl || ''}`.trim();
    const oneOffPaymentMethod = `${priceData?.oneOffPaymentMethod || ''}`.trim().toLowerCase();
    const hasRecurringValue = has_positive_payment_value([
        priceData?.recurringTotalWithVat,
        priceData?.recurringNetAmount,
        priceData?.mixedRecurringManualNetAmount,
        priceData?.mixedRecurringBaseNetAmount
    ]);
    const hasOneOffValue = has_positive_payment_value([
        priceData?.oneOffTotalWithVat,
        priceData?.oneOffNetAmount,
        priceData?.mixedOneOffManualNetAmount,
        priceData?.mixedOneOffBaseNetAmount
    ]);
    const hasPaymentValue = hasRecurringValue || hasOneOffValue;
    const hasRecurringSignal = priceData?.hasRecurringServices === true
        || recurringPaymentLinkUrl.length > 0
        || `${paymentState?.recurring || ''}`.trim().length > 0;
    const hasOneOffSignal = priceData?.hasOneOffServices === true
        || oneOffPaymentLinkUrl.length > 0
        || priceData?.oneOffPaymentUsesBankTransfer === true
        || oneOffPaymentMethod === 'bank_transfer'
        || oneOffPaymentMethod === 'payment_link'
        || `${paymentState?.oneOff || paymentState?.one_off || ''}`.trim().length > 0;
    const hasRecurring = hasPaymentValue ? hasRecurringValue : hasRecurringSignal;
    const hasOneOff = hasPaymentValue ? hasOneOffValue : !hasRecurring && hasOneOffSignal;

    if (!hasRecurring && !hasOneOff) {
        return {
            hasRecurring: true,
            hasOneOff: false
        };
    }

    return {
        hasRecurring,
        hasOneOff
    };
};

const payment_update_state = (offerItem = {}, paymentKey = '', typeKey = '') => {
    const normalizedPaymentKey = read_normalized_value(paymentKey);
    const lookupTypes = offer_type_keys_for_lookup(typeKey);
    const updates = Array.isArray(offerItem?.offerUpdates) ? offerItem.offerUpdates : [];
    const matchingUpdates = updates.filter((updateItem) => {
        const updateType = read_normalized_value(updateItem?.updateType);
        const paymentType = read_normalized_value(updateItem?.paymentType);
        const offerType = offer_type_key(updateItem?.offerType);
        const paymentMatches = paymentType === normalizedPaymentKey
            || (normalizedPaymentKey === 'one_off' && paymentType === 'one-off')
            || (normalizedPaymentKey === 'one_off' && paymentType === 'oneoff');
        const typeMatches = !lookupTypes.length || !offerType.length || lookupTypes.includes(offerType);

        return paymentMatches
            && typeMatches
            && (updateType === 'payment_paid' || updateType === 'payment_initialised');
    });
    const paidUpdate = matchingUpdates.find((updateItem) => read_normalized_value(updateItem?.updateType) === 'payment_paid');

    if (paidUpdate) {
        return 'paid';
    }

    if (matchingUpdates.length) {
        return 'initialised';
    }

    return 'pending';
};

const legacy_final_paid_at = (offerItem = {}) => {
    const marketingSetAt = `${offerItem?.marketingSetAt || ''}`.trim();
    const onboardedAt = `${offerItem?.onboardedAt || ''}`.trim();

    if (marketingSetAt.length) {
        return marketingSetAt;
    }

    if (onboardedAt.length) {
        return onboardedAt;
    }

    const updates = Array.isArray(offerItem?.offerUpdates) ? offerItem.offerUpdates : [];
    const finalUpdate = updates.find((updateItem) => {
        const updateType = read_normalized_value(updateItem?.updateType);
        return updateType === 'marketing_set' || updateType === 'offer_onboarded';
    });

    return `${finalUpdate?.occurredAt || ''}`.trim();
};

const payment_status_by_type = (offerItem = {}, paymentType = '') => {
    const normalizedStatus = read_normalized_value(offerItem?.status);
    const normalizedPaymentStatus = read_normalized_value(offerItem?.paymentStatus);
    const paymentProfile = offer_payment_profile(offerItem);
    const paymentState = offerItem?.paymentState || {};
    const recurringState = read_normalized_value(paymentState?.recurring);
    const oneOffState = read_normalized_value(paymentState?.oneOff || paymentState?.one_off);
    const updateState = payment_update_state(offerItem, paymentType);

    if (paymentType === 'recurring' && !paymentProfile.hasRecurring) {
        return 'not_applicable';
    }

    if (paymentType === 'one_off' && !paymentProfile.hasOneOff) {
        return 'not_applicable';
    }

    const targetState = paymentType === 'recurring' ? recurringState : oneOffState;
    const invalidStates = ['invalid', 'expired', 'cancelled', 'canceled', 'lost'];

    if (invalidStates.includes(targetState) || invalidStates.includes(normalizedStatus)) {
        return 'invalid';
    }

    if (legacy_final_paid_at(offerItem).length || targetState === 'paid' || updateState === 'paid') {
        return 'paid';
    }

    if (targetState === 'initialised' || updateState === 'initialised') {
        return 'initialised';
    }

    if (paymentType === 'recurring' && (normalizedStatus === 'paid' || normalizedPaymentStatus === 'paid')) {
        return 'paid';
    }

    if (paymentType === 'recurring' && (
        normalizedStatus === 'initialised'
        || normalizedStatus === 'initialized'
        || normalizedStatus === 'paid_initialised'
        || normalizedStatus === 'payment_initialised'
        || normalizedStatus === 'paid_initialized'
        || normalizedStatus === 'payment_initialized'
        || normalizedPaymentStatus === 'initialised'
    )) {
        return 'initialised';
    }

    if (paymentType === 'one_off' && !paymentProfile.hasRecurring && (normalizedStatus === 'paid' || normalizedPaymentStatus === 'paid')) {
        return 'paid';
    }

    if (paymentType === 'one_off' && !paymentProfile.hasRecurring && (
        normalizedStatus === 'initialised'
        || normalizedStatus === 'initialized'
        || normalizedStatus === 'paid_initialised'
        || normalizedStatus === 'payment_initialised'
        || normalizedStatus === 'paid_initialized'
        || normalizedStatus === 'payment_initialized'
        || normalizedPaymentStatus === 'initialised'
    )) {
        return 'initialised';
    }

    return 'valid';
};

const offer_payment_status = (offerItem = {}) => {
    const recurringStatus = payment_status_by_type(offerItem, 'recurring');
    const oneOffStatus = payment_status_by_type(offerItem, 'one_off');

    if (recurringStatus !== 'not_applicable') {
        return recurringStatus;
    }

    if (oneOffStatus !== 'not_applicable') {
        return oneOffStatus;
    }

    return 'valid';
};

const timeline_last_status = (offerItem = {}) => {
    const timeline = Array.isArray(offerItem?.timeline) ? offerItem.timeline : [];
    const normalizedLastStatus = read_normalized_value(timeline[timeline.length - 1]?.status || '');

    if (normalizedLastStatus === 'payment_initialised') {
        return 'initialised';
    }

    if (normalizedLastStatus === 'payment_confirmed') {
        return 'paid';
    }

    if (normalizedLastStatus === 'sent') {
        return 'valid';
    }

    return normalizedLastStatus;
};

const current_board_status = (offerItem = {}) => {
    const requestStatus = request_task_status(offerItem);
    const timelineStatus = timeline_last_status(offerItem);
    const rootStatus = read_normalized_value(offerItem?.status);
    const reviewStage = offer_review_stage(offerItem);

    if (requestStatus.length) {
        return requestStatus;
    }

    if (timelineStatus.length) {
        return timelineStatus;
    }

    if (reviewStage === 'reviewed' && ['help', 'requested', 'preoffer'].includes(rootStatus)) {
        return 'reviewed';
    }

    if (rootStatus === 'help' || rootStatus === 'requested' || rootStatus === 'preoffer') {
        return rootStatus;
    }

    if (rootStatus === 'draft' || rootStatus === 'ready_for_review') {
        return rootStatus;
    }

    return offer_payment_status(offerItem) || offer_current_status(offerItem);
};

const workflow_status_label = (statusKey = '') => {
    if (statusKey === 'requested') {
        return 'pre-offer';
    }

    if (statusKey === 'help') {
        return 'request help';
    }

    if (statusKey === 'preoffer') {
        return 'request help';
    }

    return statusKey;
};

const read_date_time = (dateValue = '') => {
    const timeValue = new Date(dateValue).getTime();

    if (Number.isNaN(timeValue)) {
        return 0;
    }

    return timeValue;
};

const date_part = (value = 0) => {
    return `${value}`.padStart(2, '0');
};

const format_offer_history_date = (dateValue = '') => {
    const timeValue = read_date_time(dateValue);

    if (!timeValue) {
        return '';
    }

    const dateItem = new Date(timeValue);

    return [
        `${dateItem.getFullYear()}/${date_part(dateItem.getMonth() + 1)}/${date_part(dateItem.getDate())}`,
        `${date_part(dateItem.getHours())}/${date_part(dateItem.getMinutes())}`
    ].join(' ');
};

const event_date_value = (eventItem = {}) => {
    return eventItem?.occurredAt
        || eventItem?.createdAt
        || eventItem?.updatedAt
        || eventItem?.at
        || eventItem?.date
        || eventItem?.timestamp
        || eventItem?.time
        || '';
};

const event_status_value = (eventItem = {}) => {
    return eventItem?.status
        || eventItem?.updateType
        || eventItem?.type
        || eventItem?.event
        || eventItem?.action
        || eventItem?.toStatus
        || eventItem?.to
        || eventItem?.value
        || '';
};

const offer_created_value = (offerItem = {}) => {
    const statusline = read_array(offerItem?.statusline);
    const timeline = read_array(offerItem?.timeline);

    return offerItem?.createdAt
        || offerItem?.offerCreatedAt
        || offerItem?.created_at
        || offerItem?.typesData?.createdAt
        || offerItem?.setupData?.createdAt
        || event_date_value(statusline[0])
        || event_date_value(timeline[0])
        || offerItem?.updatedAt
        || '';
};

const offer_created_time = (offerItem = {}) => {
    return read_date_time(offer_created_value(offerItem));
};

const offer_created_label = (offerItem = {}) => {
    return format_offer_history_date(offer_created_value(offerItem)) || '-';
};

const status_history_key = (statusKey = '') => {
    return read_normalized_value(statusKey).replace(/[\s-]+/g, '_');
};

const status_history_label = (statusKey = '') => {
    const normalizedStatus = status_history_key(statusKey);
    const statusLookup = {
        valid: 'Created',
        sent: 'Created',
        created: 'Created',
        paid: 'Paid',
        payment_paid: 'Paid',
        payment_confirmed: 'Paid',
        marketing_set: 'Paid',
        offer_onboarded: 'Paid',
        payment_initialised: 'Initialised',
        paid_initialised: 'Initialised',
        payment_initialized: 'Initialised',
        paid_initialized: 'Initialised',
        initialised: 'Initialised',
        initialized: 'Initialised',
        invalid: 'Lost',
        expired: 'Lost',
        cancelled: 'Lost',
        canceled: 'Lost',
        lost: 'Lost',
        help: 'request help',
        requested: 'pre-offer',
        request: 'pre-offer',
        preoffer: 'request help'
    };

    return statusLookup[normalizedStatus] || workflow_status_label(normalizedStatus);
};

const history_row_from_event = (eventItem = {}) => {
    const statusKey = event_status_value(eventItem);
    const dateValue = event_date_value(eventItem);
    const timeValue = read_date_time(dateValue);
    const statusLabel = status_history_label(statusKey);
    const dateLabel = format_offer_history_date(dateValue);

    if (!timeValue || !dateLabel.length || !`${statusLabel || ''}`.trim().length) {
        return null;
    }

    return {
        time: timeValue,
        statusLabel,
        dateLabel
    };
};

const offer_status_history_rows = (offerItem = {}) => {
    const historyRows = [
        ...read_array(offerItem?.statusline).map(history_row_from_event),
        ...read_array(offerItem?.timeline).map(history_row_from_event),
        ...read_array(offerItem?.offerUpdates).map(history_row_from_event)
    ];

    if (`${offerItem?.marketingSetAt || ''}`.trim().length) {
        historyRows.push(history_row_from_event({
            updateType: 'marketing_set',
            occurredAt: offerItem.marketingSetAt
        }));
    }

    if (`${offerItem?.onboardedAt || ''}`.trim().length) {
        historyRows.push(history_row_from_event({
            updateType: 'offer_onboarded',
            occurredAt: offerItem.onboardedAt
        }));
    }

    const sortedRows = historyRows.filter(Boolean).sort((leftItem, rightItem) => leftItem.time - rightItem.time);
    const dedupedRows = [];

    sortedRows.forEach((rowItem) => {
        const previousItem = dedupedRows[dedupedRows.length - 1];
        const previousStatus = read_normalized_value(previousItem?.statusLabel);
        const currentStatus = read_normalized_value(rowItem.statusLabel);

        if (previousStatus === currentStatus) {
            return;
        }

        dedupedRows.push({
            ...rowItem,
            key: `${rowItem.time}_${currentStatus}_${dedupedRows.length}`
        });
    });

    return dedupedRows;
};

const offer_status_history_sort_time = (offerItem = {}) => {
    const historyRows = offer_status_history_rows(offerItem);
    const lastRow = historyRows[historyRows.length - 1];

    return lastRow?.time || 0;
};

const visit_label = (offerItem = {}) => {
    const visits = offer_visit_count(offerItem);

    if (visits === 0) {
        return '0 visits (unvisited)';
    }

    if (visits === 1) {
        return '1 visit';
    }

    return `${visits} visits`;
};

const offer_status_rows = (offerItem = {}) => {
    const paymentProfile = offer_payment_profile(offerItem);
    const rows = [];

    if (paymentProfile.hasRecurring) {
        rows.push({
            label: 'Recurring',
            status: payment_status_by_type(offerItem, 'recurring')
        });
    }

    if (paymentProfile.hasOneOff) {
        rows.push({
            label: 'One-off',
            status: payment_status_by_type(offerItem, 'one_off')
        });
    }

    return rows;
};

const offer_matches_agency = (offerItem = {}) => {
    const selectedTypes = selected_offer_types(offerItem);
    const hasSass = selectedTypes.includes('sass');
    const hasAgencyType = selectedTypes.some((typeKey) => {
        return ['marketing', 'website', 'website_creation', 'website_maintenance', 'creative'].includes(typeKey);
    });

    if (hasAgencyType) {
        return true;
    }

    return !hasSass;
};

const agency_column_key = (offerItem = {}) => {
    const statusKey = current_board_status(offerItem);

    if (statusKey === 'draft' || statusKey === 'ready_for_review') {
        return '';
    }

    if (statusKey === 'invalid') {
        return 'lost';
    }

    if (statusKey === 'paid') {
        return 'paid';
    }

    if (statusKey === 'initialised') {
        return 'initialised';
    }

    if (statusKey === 'reviewed') {
        return 'reviewed';
    }

    if (statusKey === 'requested' || statusKey === 'help' || statusKey === 'preoffer') {
        return 'uncompleted';
    }

    return 'created';
};

const sorted_board_offers = (offers = []) => {
    return sort_offers_by_created_at(offers);
};

const board_offers_by_column = (offers = []) => {
    return agencyBoardColumns.reduce((accumulator, columnItem) => {
        return {
            ...accumulator,
            [columnItem.key]: sorted_board_offers(offers.filter((offerItem) => {
                return agency_column_key(offerItem) === columnItem.key;
            }))
        };
    }, {});
};

const offer_payment_summary = (offerItem = {}) => {
    const statusRows = offer_status_rows(offerItem);

    return statusRows.map((statusItem) => {
        return `${statusItem.label}: ${statusItem.status}`;
    }).join(' ');
};

const table_sort_value = (offerItem = {}, sortKey = '') => {
    if (sortKey === 'code') {
        return offer_reference(offerItem);
    }

    if (sortKey === 'brand') {
        return offer_brand(offerItem);
    }

    if (sortKey === 'email') {
        return offer_email(offerItem);
    }

    if (sortKey === 'created') {
        return offer_created_time(offerItem);
    }

    if (sortKey === 'status') {
        return workflow_status_label(current_board_status(offerItem));
    }

    if (sortKey === 'visits') {
        return offer_visit_count(offerItem);
    }

    if (sortKey === 'payment') {
        return offer_payment_summary(offerItem);
    }

    if (sortKey === 'cerebro') {
        const boardStatus = current_board_status(offerItem);
        const preofferStatus = ['help', 'requested', 'preoffer'].includes(boardStatus);

        return preofferStatus ? preoffer_sync_status(offerItem) : cerebro_connection_status(offerItem);
    }

    if (sortKey === 'history') {
        return offer_status_history_sort_time(offerItem);
    }

    return new Date(offerItem?.updatedAt || offerItem?.createdAt || 0).getTime();
};

const compare_table_values = (leftValue, rightValue) => {
    const leftNumber = typeof leftValue === 'number' ? leftValue : Number.NaN;
    const rightNumber = typeof rightValue === 'number' ? rightValue : Number.NaN;

    if (!Number.isNaN(leftNumber) && !Number.isNaN(rightNumber)) {
        return leftNumber - rightNumber;
    }

    return `${leftValue || ''}`.localeCompare(`${rightValue || ''}`, undefined, {
        numeric: true,
        sensitivity: 'base'
    });
};

const sorted_table_offers = (offers = [], sortConfig = {}) => {
    if (!sortConfig.key) {
        return sorted_table_offers(offers, {
            key: 'created',
            direction: 'desc'
        });
    }

    return [...offers].sort((leftItem, rightItem) => {
        const compareResult = compare_table_values(
            table_sort_value(leftItem, sortConfig.key),
            table_sort_value(rightItem, sortConfig.key)
        );

        return sortConfig.direction === 'desc' ? compareResult * -1 : compareResult;
    });
};

const next_table_sort = (currentSort = {}, sortKey = '') => {
    const direction = currentSort.key === sortKey && currentSort.direction === 'asc' ? 'desc' : 'asc';

    return {
        key: sortKey,
        direction
    };
};

const table_sort_label = (sortConfig = {}, sortKey = '') => {
    if (sortConfig.key !== sortKey) {
        return '';
    }

    return sortConfig.direction === 'asc' ? ' asc' : ' desc';
};

const Offers_Board_Table_Header = (props) => {
    return (
        <th>
            <OffersBoardTableHeaderButton type="button" onClick={() => props.onSort(props.sortKey)}>
                <h6>{`${props.t(props.label)}${table_sort_label(props.sortConfig, props.sortKey)}`}</h6>
            </OffersBoardTableHeaderButton>
        </th>
    );
};

const Offers_Board_Table_Row = (props) => {
    const navigate = useNavigate();
    const offerItem = props.offerItem;
    const boardStatus = current_board_status(offerItem);
    const statusLabel = workflow_status_label(boardStatus);
    const statusRows = offer_status_rows(offerItem);
    const syncStatus = preoffer_sync_status(offerItem);
    const connectionStatus = cerebro_connection_status(offerItem);
    const syncError = preoffer_sync_error(offerItem);
    const historyRows = offer_status_history_rows(offerItem);
    const canRetryPreofferSync = preoffer_sync_can_retry(offerItem, boardStatus, props.isLegacyBoard);

    const open_offer_preview = () => {
        open_offer(offerItem, boardStatus, props.routeLanguage || 'el', props.isLegacyBoard, navigate);
    };

    return (
        <tr>
            <td>
                <OffersBoardTableText>
                    <h6>{offer_reference(offerItem)}</h6>
                    {offerItem.setupData?.serviceActivation?.state === 'replaced' ? <h6>{props.t('Old / Canceled')}</h6> : null}
                </OffersBoardTableText>
            </td>
            <td>
                <OffersBoardTableText>
                    <p>{offer_brand(offerItem)}</p>
                </OffersBoardTableText>
            </td>
            <td>
                <OffersBoardTableText>
                    <p>{offer_email(offerItem) || '-'}</p>
                </OffersBoardTableText>
            </td>
            <td>
                <OffersBoardTableText>
                    <p>{offer_created_label(offerItem)}</p>
                </OffersBoardTableText>
            </td>
            <td>
                <OffersBoardTableText>
                    <p>{offer_creator_label(offerItem) || '-'}</p>
                    {offer_creator(offerItem).email ? <h6>{offer_creator(offerItem).email}</h6> : null}
                    {offer_creator_id(offerItem) ? <h6>{`${props.t('User ID')}: ${offer_creator_id(offerItem)}`}</h6> : null}
                </OffersBoardTableText>
            </td>
            <td>
                <OffersBoardTableText>
                    <p>{props.t(statusLabel)}</p>
                </OffersBoardTableText>
            </td>
            <td>
                <OffersBoardTableText>
                    <p>{visit_label(offerItem)}</p>
                </OffersBoardTableText>
            </td>
            <td>
                <OffersBoardTableText>
                    {statusRows.length ? statusRows.map((statusItem) => (
                        <h6 key={`${offer_identity_value(offerItem)}_${statusItem.label}`}>
                            {`${statusItem.label}: ${props.t(statusItem.status)}`}
                        </h6>
                    )) : <p>-</p>}
                </OffersBoardTableText>
            </td>
            <td>
                <OffersBoardTableActions>
                    {!props.isLegacyBoard && ['help', 'requested', 'preoffer'].includes(boardStatus) ? (
                        <p>{`${props.t('Turn')}: ${props.t(workflow_turn_label(offerItem) || '-')}`}</p>
                    ) : ['help', 'requested', 'preoffer'].includes(boardStatus) ? (
                        <OffersBoardTableText>
                            <p>{props.t(syncStatus)}</p>
                            {syncError.length ? <h6>{syncError}</h6> : null}
                        </OffersBoardTableText>
                    ) : <p>{props.t(connectionStatus)}</p>}
                </OffersBoardTableActions>
            </td>
            <td>
                <OffersBoardTableText>
                    {historyRows.length ? historyRows.map((historyItem) => (
                        <h6 key={`${offer_identity_value(offerItem)}_${historyItem.key}`}>
                            {`${historyItem.dateLabel} -> ${props.t(historyItem.statusLabel)}`}
                        </h6>
                    )) : <p>-</p>}
                </OffersBoardTableText>
            </td>
            <td>
                <Main_Plain
                    text={props.t('Open offer')}
                    onClick={open_offer_preview}
                />
                {canRetryPreofferSync ? (
                    <Main_Plain
                        text={props.isRetryingPreofferSync ? props.t('Retrying...') : props.t('Retry Tasks')}
                        onClick={() => props.onRetryPreofferSync(offerItem)}
                    />
                ) : null}
                {props.canManageFakeOffers && offerItem?.setupData?.fakeOffer === true ? (
                    <OffersBoardTableActions>
                        <Main_Plain text={props.t('Create replacement offer')} onClick={() => props.onEditFakeOffer(offerItem)} />
                        <Main_Plain
                            text={props.isDeletingFakeOffer ? props.t('Deleting...') : props.t('Delete fake offer')}
                            disabled={props.isDeletingFakeOffer}
                            onClick={() => props.onDeleteFakeOffer(offerItem)}
                        />
                    </OffersBoardTableActions>
                ) : null}
            </td>
        </tr>
    );
};

const Offers_Board_Table_View = (props) => {
    return (
        <OffersBoardTableWrap>
            <OffersBoardTable>
                <thead>
                    <tr>
                        <Offers_Board_Table_Header t={props.t} label="Code" sortKey="code" sortConfig={props.sortConfig} onSort={props.onSort} />
                        <Offers_Board_Table_Header t={props.t} label="Brand" sortKey="brand" sortConfig={props.sortConfig} onSort={props.onSort} />
                        <Offers_Board_Table_Header t={props.t} label="Email" sortKey="email" sortConfig={props.sortConfig} onSort={props.onSort} />
                        <Offers_Board_Table_Header t={props.t} label="Created" sortKey="created" sortConfig={props.sortConfig} onSort={props.onSort} />
                        <th><h6>{props.t('Created by')}</h6></th>
                        <Offers_Board_Table_Header t={props.t} label="Status" sortKey="status" sortConfig={props.sortConfig} onSort={props.onSort} />
                        <Offers_Board_Table_Header t={props.t} label="Visits" sortKey="visits" sortConfig={props.sortConfig} onSort={props.onSort} />
                        <Offers_Board_Table_Header t={props.t} label="Payment" sortKey="payment" sortConfig={props.sortConfig} onSort={props.onSort} />
                        <Offers_Board_Table_Header t={props.t} label="Tasks" sortKey="cerebro" sortConfig={props.sortConfig} onSort={props.onSort} />
                        <Offers_Board_Table_Header t={props.t} label="History" sortKey="history" sortConfig={props.sortConfig} onSort={props.onSort} />
                        <th><h6>{props.t('Actions')}</h6></th>
                    </tr>
                </thead>
                <tbody>
                    {!props.isLoading && !props.offers.length ? (
                        <tr>
                            <td colSpan={11}>
                                <OffersBoardTableEmpty>
                                    <p>{props.t('No offers found.')}</p>
                                </OffersBoardTableEmpty>
                            </td>
                        </tr>
                    ) : null}
                    {props.offers.map((offerItem) => (
                        <Offers_Board_Table_Row
                            key={offer_identity_value(offerItem) || offer_reference(offerItem)}
                            offerItem={offerItem}
                            t={props.t}
                            routeLanguage={props.routeLanguage}
                            isLegacyBoard={props.isLegacyBoard}
                            isRetryingPreofferSync={props.retryingPreofferSyncId === offer_preview_identity_value(offerItem)}
                            onRetryPreofferSync={props.onRetryPreofferSync}
                            canManageFakeOffers={props.canManageFakeOffers}
                            isDeletingFakeOffer={props.deletingFakeOfferId === offer_preview_identity_value(offerItem)}
                            onEditFakeOffer={props.onEditFakeOffer}
                            onDeleteFakeOffer={props.onDeleteFakeOffer}
                        />
                    ))}
                </tbody>
            </OffersBoardTable>
        </OffersBoardTableWrap>
    );
};

const Agency_Board_Content = (props) => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [offers, setOffers] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [errorMessage, setErrorMessage] = useState('');
    const [searchInput, setSearchInput] = useState('');
    const [searchValue, setSearchValue] = useState('');
    const [viewMode, setViewMode] = useState('columns');
    const [retryingPreofferSyncId, setRetryingPreofferSyncId] = useState('');
    const [deletingFakeOfferId, setDeletingFakeOfferId] = useState('');
    const requestSequenceRef = useRef(0);
    const initialLoadCompletedRef = useRef(false);
    const loadingColumnsRef = useRef(new Set());
    const paginationRef = useRef(empty_column_pagination());
    const scrollEndReachedRef = useRef(new WeakSet());
    const [tableSort, setTableSort] = useState({
        key: 'created',
        direction: 'desc'
    });
    const isLegacyBoard = props.boardMode === 'legacy';
    const visibleOffers = isLegacyBoard ? offers : offers.filter((offerItem) => offer_matches_agency(offerItem));
    const tableOffers = sorted_table_offers(visibleOffers, tableSort);
    const offersByColumn = board_offers_by_column(visibleOffers);

    const list_column_page = (columnKey = '', cursor = '') => {
        const requestParams = {
            ...search_params(searchValue),
            column: columnKey,
            ...(cursor.length ? { cursor } : {})
        };

        return isLegacyBoard
            ? list_legacy_board_offer_page(requestParams)
            : list_agency_board_offer_page(requestParams);
    };

    const get_offers = async() => {
        const requestSequence = requestSequenceRef.current + 1;

        requestSequenceRef.current = requestSequence;
        loadingColumnsRef.current = new Set();
        paginationRef.current = empty_column_pagination();
        scrollEndReachedRef.current = new WeakSet();
        setIsLoading(true);
        setErrorMessage('');

        try {
            const responses = await Promise.all(agencyBoardColumns.map(async(columnItem) => {
                const response = await list_column_page(columnItem.key);

                return {
                    columnKey: columnItem.key,
                    page: response?.data?.data || {}
                };
            }));

            if (requestSequence !== requestSequenceRef.current) {
                return;
            }

            const nextPagination = empty_column_pagination();
            const firstOffers = [];

            responses.forEach((responseItem) => {
                const pageOffers = Array.isArray(responseItem.page?.items) ? responseItem.page.items : [];
                const nextCursor = responseItem.page?.nextCursor || '';

                firstOffers.push(...pageOffers);
                nextPagination[responseItem.columnKey] = {
                    nextCursor,
                    hasMore: Boolean(responseItem.page?.hasMore && nextCursor)
                };
            });

            paginationRef.current = nextPagination;
            setOffers(merge_offer_board_rows([], firstOffers));
        } catch (error) {
            if (requestSequence === requestSequenceRef.current) {
                setOffers([]);
                setErrorMessage(t('Could not load offers.'));
            }
        } finally {
            if (requestSequence === requestSequenceRef.current) {
                initialLoadCompletedRef.current = true;
                setIsLoading(false);
            }
        }
    };

    const load_more = async(columnKey = '') => {
        const pagination = paginationRef.current[columnKey] || {};

        if (isLoading || loadingColumnsRef.current.has(columnKey) || !pagination.hasMore || !pagination.nextCursor) {
            return;
        }

        const requestSequence = requestSequenceRef.current;
        const loadingColumns = loadingColumnsRef.current;

        loadingColumns.add(columnKey);
        setErrorMessage('');

        try {
            const response = await list_column_page(columnKey, pagination.nextCursor);

            if (requestSequence !== requestSequenceRef.current) {
                return;
            }

            const responsePage = response?.data?.data || {};
            const pageOffers = Array.isArray(responsePage?.items) ? responsePage.items : [];
            const nextCursor = responsePage?.nextCursor || '';

            paginationRef.current = {
                ...paginationRef.current,
                [columnKey]: {
                    nextCursor,
                    hasMore: Boolean(responsePage?.hasMore && nextCursor)
                }
            };
            setOffers((currentOffers) => merge_offer_board_rows(currentOffers, pageOffers));
        } catch (error) {
            if (requestSequence === requestSequenceRef.current) {
                setErrorMessage(t('Could not load offers.'));
            }
        } finally {
            loadingColumns.delete(columnKey);
        }
    };

    const load_more_on_scroll = (event, columnKey = '') => {
        const scrollElement = event.currentTarget;
        const scrollDistance = scrollElement.scrollHeight - scrollElement.clientHeight;
        const isNearScrollEnd = scrollDistance > 0 && scrollDistance - scrollElement.scrollTop <= 200;

        if (!isNearScrollEnd) {
            scrollEndReachedRef.current.delete(scrollElement);
            return;
        }

        if (scrollEndReachedRef.current.has(scrollElement)) {
            return;
        }

        scrollEndReachedRef.current.add(scrollElement);
        load_more(columnKey);
    };

    useEffect(() => {
        get_offers();

        return () => {
            requestSequenceRef.current += 1;
            loadingColumnsRef.current = new Set();
            paginationRef.current = empty_column_pagination();
        };
    }, [t, isLegacyBoard, searchValue]);

    useEffect(() => {
        const timeoutId = window.setTimeout(() => {
            setSearchValue(searchInput);
        }, 350);

        return () => {
            window.clearTimeout(timeoutId);
        };
    }, [searchInput]);

    const sort_table = (sortKey = '') => {
        setTableSort((currentSort) => next_table_sort(currentSort, sortKey));
    };

    const retry_preoffer_sync = async(offerItem = {}) => {
        const offerSyncId = offer_preview_identity_value(offerItem);

        if (!offerSyncId.length || retryingPreofferSyncId.length) {
            return;
        }

        setRetryingPreofferSyncId(offerSyncId);
        setErrorMessage('');

        try {
            await retry_agency_preoffer_sync_by_id(offerSyncId);
            await get_offers();
        } catch (error) {
            setErrorMessage(t('Could not retry Tasks sync.'));
        } finally {
            setRetryingPreofferSyncId('');
        }
    };

    const edit_fake_offer = (offerItem = {}) => {
        navigate(`/${props.routeLanguage}/new-offer?fake=1`);
    };

    const delete_fake_offer = async(offerItem = {}) => {
        const offerId = offer_preview_identity_value(offerItem);
        const offerCode = offer_reference(offerItem);

        if (!props.canManageFakeOffers || !offerId || deletingFakeOfferId || !window.confirm(t('Delete fake offer {{code}}? This removes it from Offers, but historical Niffler timeline entries and Stripe subscriptions remain.', { code: offerCode }))) {
            return;
        }

        setDeletingFakeOfferId(offerId);
        setErrorMessage('');

        try {
            await delete_fake_offer_by_id(offerId);
            await get_offers();
        } catch (error) {
            setErrorMessage(error?.response?.data?.message || t('Could not delete the fake offer.'));
        } finally {
            setDeletingFakeOfferId('');
        }
    };

    const render_offer_card = (offerItem = {}) => {
        const boardStatus = current_board_status(offerItem);
        const offerId = offer_identity_value(offerItem) || offer_reference(offerItem);

        return (
            <Offers_Board_Card
                key={offerId}
                t={t}
                offerItem={offerItem}
                offerId={offerId}
                offerReference={offer_reference(offerItem)}
                offerBrand={offer_brand(offerItem)}
                offerEmail={offer_email(offerItem)}
                offerType={offer_type_label(offerItem)}
                fakeOffer={offerItem?.setupData?.fakeOffer === true}
                submitterLabel={offer_creator_label(offerItem)}
                reviewerLabel={offer_reviewer_label(offerItem)}
                creatorLabel={offer_completed_creator_label(offerItem)}
                previewOfferId={offer_preview_identity_value(offerItem)}
                routeLanguage={props.routeLanguage}
                viewOnly={!isLegacyBoard}
                continueOffer={offer_should_open_as_draft(offerItem, boardStatus, isLegacyBoard)}
                boardStatus={boardStatus}
                showVisits={offer_card_shows_visits(offerItem, boardStatus)}
                visitLabel={visit_label(offerItem)}
                actionText={isLegacyBoard ? '' : offer_review_action(offerItem)}
                cerebroHandoff={!isLegacyBoard && offer_has_pending_cerebro_phase(offerItem)}
                cerebroTaskUrl={offer_cerebro_task_url(offerItem, config.cerebro, props.routeLanguage)}
                isRetryingPreofferSync={retryingPreofferSyncId === offer_preview_identity_value(offerItem)}
                onRetryPreofferSync={retry_preoffer_sync}
                canManageFakeOffers={!isLegacyBoard && props.canManageFakeOffers}
                isDeletingFakeOffer={deletingFakeOfferId === offer_preview_identity_value(offerItem)}
                onEditFakeOffer={edit_fake_offer}
                onDeleteFakeOffer={delete_fake_offer}
            />
        );
    };

    return (
        <OffersBoardContent>
            <PageTitle>
                <h1>{props.title}</h1>
            </PageTitle>
            <OffersBoardActions>
                <OffersBoardSearch>
                    <Input_
                        value={searchInput}
                        onChange={(event) => setSearchInput(event.target.value)}
                        placeholder={t('Search offers or emails')}
                    />
                </OffersBoardSearch>
                <Board_View_Toggle
                    t={t}
                    viewMode={viewMode}
                    onViewModeChange={setViewMode}
                />
            </OffersBoardActions>
            {errorMessage.length ? <p>{errorMessage}</p> : null}
            {isLoading && !initialLoadCompletedRef.current ? <Main_Loader></Main_Loader> : null}
            <OffersBoardStage>
                {viewMode === 'table' ? (
                    <Offers_Board_Table_View
                        t={t}
                        offers={tableOffers}
                        isLoading={isLoading}
                        routeLanguage={props.routeLanguage}
                        isLegacyBoard={isLegacyBoard}
                        sortConfig={tableSort}
                        onSort={sort_table}
                        retryingPreofferSyncId={retryingPreofferSyncId}
                        onRetryPreofferSync={retry_preoffer_sync}
                        canManageFakeOffers={!isLegacyBoard && props.canManageFakeOffers}
                        deletingFakeOfferId={deletingFakeOfferId}
                        onEditFakeOffer={edit_fake_offer}
                        onDeleteFakeOffer={delete_fake_offer}
                    />
                ) : (
                    <OffersBoardColumns columnCount={agencyBoardColumns.length}>
                        {agencyBoardColumns.map((columnItem) => {
                            const columnOffers = offersByColumn[columnItem.key] || [];

                            return (
                                <OffersBoardColumn
                                    key={columnItem.key}
                                    data-column={columnItem.key}
                                    onScroll={(event) => load_more_on_scroll(event, columnItem.key)}
                                >
                                    <OffersBoardColumnHeader>
                                        <OffersBoardColumnTitle>
                                            <h4>{t(columnItem.title)}</h4>
                                        </OffersBoardColumnTitle>
                                        <OffersBoardColumnCount>
                                            <h6>{columnOffers.length}</h6>
                                        </OffersBoardColumnCount>
                                    </OffersBoardColumnHeader>
                                    <OffersBoardColumnBody>
                                        {!isLoading && !columnOffers.length ? (
                                            <OffersBoardEmpty>
                                                <p>{t('No offers in this column.')}</p>
                                            </OffersBoardEmpty>
                                        ) : null}
                                        {columnOffers.map((offerItem) => {
                                            return render_offer_card(offerItem);
                                        })}
                                    </OffersBoardColumnBody>
                                </OffersBoardColumn>
                            );
                        })}
                    </OffersBoardColumns>
                )}
            </OffersBoardStage>
        </OffersBoardContent>
    );
};

export const Agency_Board = (props) => {
    return <Agency_Board_Content {...props} />;
};

export const Legacy_Board = (props) => {
    return <Agency_Board_Content {...props} boardMode="legacy" />;
};

export const Offers_Board = (props) => {
    return <Agency_Board {...props} />;
};
